import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import pick from 'lodash/pick';
import get from 'lodash/get';
import forEach from 'lodash/forEach';

import {
  openOneTrustSettingsDialogOnInit,
  setOneTrustGdprData,
} from '../../actions/app';
import { logCategoryActionLabel, logClientError } from '../../actions/logging';

import isServer from '../../utils/isServer';
import {
  removeInactiveConsentCategoryCookies,
  setCookie,
} from '../../utils/cookie';
import reloadPage from '../../../client/utils/reloadPage';

import { activeConsentCategoriesCookie } from '../../constants/cookies';
import consentCookieCategories from '../../constants/ads/consentCookieCategories';
import feature from '../../constants/analytics/categoryActionLabel/feature';
import {
  CMP_API_VERSION,
  cmpApiCommands,
  events,
  gdprApplicableCountryCodes,
  ONETRUST_SOURCE,
  oneTrustMethods,
  vendorConsentHandlers,
} from './constants/oneTrust';

const ONETRUST_APIS_TIMEOUT = 5000;
const ONETRUST_INTERVALS_DELAY = 100;

/**
 * Manages user GDPR consent via the APIs provided by the OneTrust library.
 * CMP API specification: https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md
 *
 * @param {boolean} isMobileBlockerFlow - When true, closes GDPR banner until mobile blocker
 *  is closed. See AppConnector.js.
 * @param {boolean} shouldDialogOpen - When toggled to true, requests dialog to open
 *  (triggered from footer link and Gemini-Desktop menu)
 * @param {boolean} openDialogOnInit - Set to true by the mobile blocker flow via
 *  openOneTrustSettingsDialogOnInit()
 * @param {string} dataSubjectId - The user ID or serial to associate with the OneTrust user profile
 * @param {boolean} gdprApplies - Determined the OneTrust's geolocation helper and a static list of
 *  EU countries
 * @param {object} actions
 *  @property {function} setOneTrustGdprData - Primary action creator to set GDPR consent data in
 *  the redux store
 *  @property {function} openOneTrustSettingsDialogOnInit - Action creator that signals dialog to
 *  open after mobile blocker is closed.
 *  @property {function} logClientError - Action creator that logs errors.
 */
class OneTrust extends Component {
  static propTypes = {
    isMobileBlockerFlow: PropTypes.bool,

    // mapStateToProps
    shouldDialogOpen: PropTypes.bool.isRequired,
    openDialogOnInit: PropTypes.bool.isRequired,
    dataSubjectId: PropTypes.string.isRequired,
    gdprApplies: PropTypes.bool,

    // mapDispatchToProps
    actions: PropTypes.shape({
      setOneTrustGdprData: PropTypes.func.isRequired,
      openOneTrustSettingsDialogOnInit: PropTypes.func.isRequired,
      logClientError: PropTypes.func.isRequired,
    }),
  };

  constructor(props) {
    super(props);

    if (isServer()) {
      return;
    }

    this.startTime = Date.now();
    this.eventListenerIds = {};

    this.handleTcData = this.handleTcData.bind(this);
    this.handleOneTrustError = this.handleOneTrustError.bind(this);
    this.determineGdprApplicability =
      this.determineGdprApplicability.bind(this);
    this.determineGdprConsent = this.determineGdprConsent.bind(this);
    this.reportAndReloadPage = this.reportAndReloadPage.bind(this);
    this.init = props.isMobileBlockerFlow
      ? this.mobileBlockerInit.bind(this)
      : this.init.bind(this);

    this.init().catch(this.handleOneTrustError);
  }

  componentDidUpdate(prevProps) {
    const { shouldDialogOpen, dataSubjectId, gdprApplies } = this.props;

    if (shouldDialogOpen && !prevProps.shouldDialogOpen) {
      this.invokeOneTrustMethod(oneTrustMethods.openSettingsDialog);
    }

    if (dataSubjectId !== prevProps.dataSubjectId && gdprApplies) {
      this.invokeOneTrustMethod(
        oneTrustMethods.setDataSubjectId,
        dataSubjectId,
      );
    }
  }

  get oneTrust() {
    // eslint-disable-next-line no-undef,no-underscore-dangle
    return window.OneTrust;
  }

  /**
   * Sets/removes cookies based on consent and updates redux store with consent preferences
   *
   * @param {object} tcData
   * @param {boolean} hasConsentBeenCaptured
   */
  setConsentData(tcData = {}, hasConsentBeenCaptured = true) {
    const { actions } = this.props;
    setCookie(activeConsentCategoriesCookie.name, null, this.activeGroups);
    removeInactiveConsentCategoryCookies();
    this.updateManualConsentVendors(tcData);

    // TODO:
    //  This targeting-cookie-based consent strategy is meant to be replaced by a more
    //  careful, granular purpose-based consent strategy, guided by the IAB Global Vendor List:
    //  https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20Consent%20string%20and%20vendor%20list%20formats%20v2.md#the-global-vendor-list
    //  https://vendorlist.consensu.org/v2/vendor-list.json
    const isOptedOut = !this.activeGroups.includes(
      consentCookieCategories.targeting,
    );

    actions.setOneTrustGdprData({
      isOptedOut,
      tcString: tcData.tcString,
      gdprApplies: true,
      shouldDialogOpen: false,
      hasConsentBeenCaptured,
    });
  }

  /**
   * Requests TC Data from OneTrust's CMP API (window.__tcfapi)
   *
   * @returns {Promise<object|Error>}
   */
  getTcData() {
    return new Promise((resolve, reject) => {
      this.tcfApiDelegate(cmpApiCommands.getTCData, null, { resolve, reject });
    });
  }

  /**
   * For vendors scripts/libraries that require consent signals, but do not implement TCF V2, we
   * should register a handler function for that vendor that will appropriately communicate user
   * consent. This method iterates over these handlers and attempts to pass along the appropriate
   * user consent signal for that vendor.
   *
   * @param {object} tcData
   */
  updateManualConsentVendors(tcData) {
    const vendorConsents = get(tcData, 'vendor.consents', {});

    forEach(vendorConsentHandlers, (vendorConsentHandler, vendorId) => {
      const vendorConsent = vendorConsents[vendorId] || false;

      try {
        // handlers should be defined with a single, opt-out-oriented Boolean parameter
        vendorConsentHandler(!vendorConsent);
      } catch (e) {
        this.handleOneTrustError(e);
      }
    });
  }

  /**
   * Invokes a method available on the OneTrust API
   *
   * @param {string} method - OneTrust API method name
   * @param {...*} args - arguments to be passed into the method
   * @returns {*} - return value of the method call
   */
  // eslint-disable-next-line consistent-return
  invokeOneTrustMethod(method, ...args) {
    try {
      return this.oneTrust[method](...args);
    } catch (e) {
      e.context = { method };
      this.handleOneTrustError(e);
    }
  }

  get tcfApi() {
    // eslint-disable-next-line no-undef,no-underscore-dangle
    return window.__tcfapi;
  }

  get activeGroups() {
    // eslint-disable-next-line no-undef
    return window.OnetrustActiveGroups || '';
  }

  /**
   * Determines GDPR applicability and user consent.
   */
  async init() {
    const gdprApplies = await this.determineGdprApplicability();

    if (!gdprApplies) {
      this.optUserIn();
      return;
    }

    await this.determineGdprConsent();
  }

  /**
   * Cyclically checks for the availability of the OneTrust API, then determines GDPR applicability
   * via the provided geolocation method.
   */
  determineGdprApplicability() {
    return new Promise((res, rej) => {
      if (Date.now() - this.startTime > ONETRUST_APIS_TIMEOUT) {
        this.optUserIn();
        rej(
          new Error(
            'OneTrust API did not load within the allotted time window.',
          ),
        );
        return;
      }

      if (!this.oneTrust) {
        setTimeout(
          () => this.determineGdprApplicability().then(res).catch(rej),
          ONETRUST_INTERVALS_DELAY,
        );
        return;
      }

      const geoLocationData = this.invokeOneTrustMethod('getGeolocationData');
      const gdprApplies = gdprApplicableCountryCodes.hasOwnProperty(
        geoLocationData.country,
      );
      res(gdprApplies);
    });
  }

  async reportAndReloadPage(tcData) {
    await this.props.actions.logCategoryActionLabel({
      category: feature.category,
      action: feature.actions.gdpr,
      label: tcData.tcString,
    });

    reloadPage();
  }

  /**
   * Cyclically checks for the availability of the OneTrust CMP API, then determines GDPR user
   * consent via event listeners registered with the API's event system. Additionally, this method
   * sets the user's subject ID and optionally opens the preference settings dialog, as determined
   * via the mobile blocker flow.
   */
  async determineGdprConsent() {
    if (Date.now() - this.startTime > ONETRUST_APIS_TIMEOUT) {
      this.optUserIn();
      throw new Error(
        "OneTrust's tcfApi did not load within the allotted time window.",
      );
    }

    if (!this.tcfApi) {
      setTimeout(this.determineGdprConsent, ONETRUST_INTERVALS_DELAY);
      return;
    }

    const tcData = await this.getTcData();

    if (!tcData) {
      this.optUserIn();
      return;
    }

    if (this.props.openDialogOnInit) {
      this.invokeOneTrustMethod(oneTrustMethods.openSettingsDialog);
    }

    this.invokeOneTrustMethod(
      oneTrustMethods.setDataSubjectId,
      this.props.dataSubjectId,
    );

    // the tcLoaded event is emitted on page load as soon as "Transparency & Consent" (GDPR) data
    // is made ready by the TCF API. See `events` definition for more information.
    this.addEventListener(events.tcLoaded, this.handleTcData);

    // the consentUpdated event is emitted whenever a user makes a change to their consent
    // preferences. The consent data is reported synchronously before reloading the page.
    // See `events` definition for more information.
    this.addEventListener(events.consentUpdated, this.reportAndReloadPage);

    // The bannerShown event is the only event first-time visitors will trigger on initial page
    // load, so we're registering this listener to make sure we diligently initialize consent for
    // those users. Listener is removed in handleTcData().
    this.addEventListener(events.bannerShown, (_tcData) =>
      this.handleTcData(_tcData, false),
    );
  }

  /**
   * Determines GDPR applicability, then blocks the banner from showing and blocking the
   * mobile blocker dialog and flipping a flag in the store to later open the preference center,
   * once the mobile blocker is closed.
   */
  async mobileBlockerInit() {
    const gdprApplies = await this.determineGdprApplicability();

    if (!gdprApplies) {
      return;
    }

    this.addEventListener(events.bannerShown, (tcData) => {
      // we need to close the banner first, or else it'll show
      this.invokeOneTrustMethod(oneTrustMethods.closeAll);
      this.removeEventListener(events.bannerShown, tcData.listenerId);
      this.props.actions.openOneTrustSettingsDialogOnInit();
    });
  }

  /**
   * Sets opt-in consent values for GDPR in the redux store.
   */
  optUserIn() {
    this.props.actions.setOneTrustGdprData({
      isOptedOut: false,
      gdprApplies: false,
    });
  }

  /**
   * Delegate function that handles command calls to the CMP API (window.__tcfapi)
   * with an internal callback that resolves the response/error with a passed-in callback
   *
   * @param {string} command - the name of the command per the CMP API specification
   * @param {string|null} eventName - the name of the event to register, if applicable
   * @param {function} callback - the callback function for the event listener
   * @param {*} [optionalParameter] - optional parameter uniquely defined per command option in the
   *  CMP API specification
   * @param {function} [apiCallbackOverride] - optional override for the internally-defined
   *  apiCallback function
   */
  tcfApiDelegate(
    command,
    eventName,
    callback,
    optionalParameter,
    apiCallbackOverride,
  ) {
    // this flexibility allows us to handle "bubbling up" errors for nested async operations.
    // See getTcData().
    let responseHandlers;
    if (typeof callback === 'function') {
      responseHandlers = {
        resolve: callback,
        reject: (e) => {
          throw e;
        },
      };
    } else {
      responseHandlers = callback;
    }

    const apiCallback = (response, success) => {
      if (!success) {
        const error =
          response instanceof Error
            ? response
            : new Error(
                typeof response === 'string'
                  ? response
                  : JSON.stringify(response),
              );

        error.context = { command, eventName, cmpApiVersion: CMP_API_VERSION };
        responseHandlers.reject(error);
      }

      if (eventName) {
        this.eventListenerIds[eventName] = response.listenerId;
      }

      if (!eventName || response.eventStatus === eventName) {
        responseHandlers.resolve(response);
      }
    };

    this.tcfApi(
      command,
      CMP_API_VERSION,
      apiCallbackOverride || apiCallback,
      optionalParameter,
    );
  }

  /**
   * A helper method to invoke the addEventListener command on the CMP API
   *
   * @param {string} eventName - the event name as defined by the CMP API specification
   * @param {function} callback - a callback to handle the response
   */
  addEventListener(eventName, callback) {
    this.tcfApiDelegate(cmpApiCommands.addEventListener, eventName, callback);
  }

  /**
   * A helper method to invoke the removeEventListener command on the CMP API
   *
   * @param {string} eventName - the event name as defined by the CMP API specification
   * @param {number} listenerId - the listener ID provided by the response to the addEventListener
   *  command
   */
  removeEventListener(eventName, listenerId) {
    const apiCallbackOverride = (success) => {
      if (!success) {
        this.handleRemoveEventListenerError(eventName);
      }
    };

    this.tcfApiDelegate(
      cmpApiCommands.removeEventListener,
      eventName,
      null,
      listenerId,
      apiCallbackOverride,
    );
  }

  /**
   * A callback for commands called on the CMP API.
   *
   * @param {object} tcData - the TC Data object as defined by the CMP API specification
   * @param {boolean} hasConsentBeenCaptured - indicates whether or not the call was user-initiated
   */
  handleTcData(tcData, hasConsentBeenCaptured = true) {
    // The bannerShown event is the only event first-time visitors will trigger on initial page
    // load. Removing the listener for this event ensures we don't update consent on user-initiated
    // banner requests. We're still listening for selection changes to capture consent updates.
    const bannerShownListenerId = this.eventListenerIds[events.bannerShown];
    if (bannerShownListenerId) {
      delete this.eventListenerIds[events.bannerShown];
      this.removeEventListener(events.bannerShown, bannerShownListenerId);
    }

    this.invokeOneTrustMethod(
      oneTrustMethods.setDataSubjectId,
      this.props.dataSubjectId,
    );
    // The timeout is required to give OneTrust enough time to set the activeGroups cookie.
    setTimeout(() => this.setConsentData(tcData, hasConsentBeenCaptured), 0);
  }

  /**
   * Generic error handler for the methods in this class.
   * Logs errors via logClientError() action creator.
   *
   * @param {error} error
   */
  handleOneTrustError(error) {
    const {
      message,
      stack,
      context = {},
    } = pick(error, ['message', 'stack', 'context']);

    this.props.actions.logClientError({
      message,
      context: {
        ...context,
        stack,
        source: ONETRUST_SOURCE,
      },
    });
  }

  /**
   * Error handler for removeEventListener command.
   *
   * @param {string} eventName
   */
  handleRemoveEventListenerError(eventName) {
    this.handleOneTrustError(
      new Error(`Error removing event listener for event: ${eventName}`),
    );
  }

  render() {
    return null;
  }
}

function mapStateToProps({ app, me }) {
  return {
    shouldDialogOpen: get(app, 'oneTrust.gdpr.shouldDialogOpen', false),
    openDialogOnInit: get(app, 'oneTrust.openSettingsDialogOnInit', false),
    gdprApplies: get(app, 'oneTrust.gdpr.gdprApplies', false),

    // when the user is logged in, we want to use their id for the Data Subject ID
    dataSubjectId: get(me, 'details.id') || get(app, 'tuneInUserSerial', ''),
  };
}

function mapDispatchToProps(dispatch) {
  const actions = {
    setOneTrustGdprData,
    openOneTrustSettingsDialogOnInit,
    logClientError,
    logCategoryActionLabel,
  };

  return { actions: bindActionCreators(actions, dispatch) };
}

const ConnectedOneTrust = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OneTrust);
const testsOnly = { OneTrust, mapStateToProps, mapDispatchToProps };
export { testsOnly, ConnectedOneTrust as OneTrust };
